import { Routes, Route, useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Modal, ActionHandler } from "@/components/UI/tools";
import WorkspaceNavbar from "@/components/navbar/workspaceNavbar";
import { appState } from "../app/appState";

// Import components
import SignIn from "@/pages/SignIn";
import InvitePage from "@/pages/InvitePage";
import { PageBox } from "@/components/UI/page";

// Import workspace components
import { BillingReturnPage } from "@/app/workspace/billing-return/BillingReturnPage";
import { SettingsPage } from "@/app/workspace/settings/settingsPage";
import ArtifactsList from "@/app/workspace/artifacts/list";
import ArtifactEntry from "@/app/workspace/artifacts/entry";
import AdminChat from "@/app/workspace/chat/chatContainer";
import TagList from "@/app/workspace/tags/list";
import TagEntry from "@/app/workspace/tags/entry";
import EventsList from "@/app/workspace/events/list";
import EventEntry from "@/app/workspace/events/entry";
import DocumentsList from "@/app/workspace/documents/list";
import DocumentEntry from "@/app/workspace/documents/entry";
import { Activity } from '@/components/activity'
import { page_artifact, page_artifact_list_item } from "@/app/typing/artifacts";
import { Request } from "@/components/CRUD";
import { getWebPages, readUsersMe } from "@/api";
import { WebPage } from "@/pages/web-page";
import { HomePage } from "@/pages/homePage";
import SignUp from "@/pages/SignUp";
import { LegalPageFallback } from "@/pages/PricingPage";
import { PublicPageShell } from "@/components/PublicPageShell";
import { keepAliveProviderTypeName } from "react-keep-alive/cjs/components/Provider";
import { Outlet } from "react-router-dom";
import { chatState } from "@/app/chatState";
import { runsState } from "@/app/runsState";
import { chatSession } from "@/aawebsocket/chatSession";
import { ErrorBoundary } from "react-error-boundary";
import { WorkspaceDashboard } from "@/app/workspace/workspaceDashboard";
import { ProfilePage } from "@/app/workspace/profile/profilePage";
import { SummarisationTestsPage } from "@/app/workspace/summarisation-tests/summarisationTestsPage";
import { ChatEvaluationPage } from "@/app/workspace/chat-evaluation/chatEvaluationPage";
import { ChatMessageTestPage } from "@/app/workspace/chat-message-test/chatMessageTestPage";
import { GalleryPage } from "@/app/workspace/gallery/galleryPage";
import { BillingLabPage } from "@/app/workspace/billing-lab/billingLabPage";
// Inline page components



const Artifacts = () => (

	<ArtifactsList />

);



const Tags = () => (

	<TagList />

);

const Events = () => (

	<EventsList />

);

const Documents = () => (

	<DocumentsList />

);

const ArtifactDetailWrapper = ({ id }: { id: string }) => {
	const navigate = useNavigate();
	const handleSetId = (newId?: number) => {
		if (newId) {
			navigate(`/workspace/artifacts/${newId}`);
		} else {
			navigate('/workspace/artifacts');
		}
	};

	return (

		<ArtifactEntry
			id={id ? parseInt(id) : undefined}
			setId={handleSetId}
		/>

	);
};

const TagDetailWrapper = ({ id }: { id: string }) => {
	const navigate = useNavigate();

	const handleSetId = (newId?: number) => {
		if (newId) {
			navigate(`/workspace/tags/${newId}`);
		} else {
			navigate('/workspace/tags');
		}
	};

	return (

		<TagEntry
			id={id ? parseInt(id) : undefined}
			setId={handleSetId}
		/>

	);
};

const EventDetailWrapper = ({ id }: { id: string }) => {
	const navigate = useNavigate();

	const handleSetId = (newId?: number) => {
		if (newId) {
			navigate(`/workspace/events/${newId}`);
		} else {
			navigate('/workspace/events');
		}
	};

	return (

		<EventEntry
			id={id ? parseInt(id) : undefined}
			setId={handleSetId}
		/>

	);
};

const DocumentDetailWrapper = ({ id }: { id: string }) => {
	const navigate = useNavigate();

	const handleSetId = (newId?: number) => {
		if (newId) {
			navigate(`/workspace/documents/${newId}`);
		} else {
			navigate('/workspace/documents');
		}
	};

	return (

		<DocumentEntry
			id={id ? parseInt(id) : undefined}
			setId={handleSetId}
		/>

	);
};

const Workspace = observer(() => {
	const location = useLocation();
	const navigate = useNavigate();
	const pathname = location.pathname;

	useEffect(() => {
		console.log("Workspace mounted");
		// A tab whose session was revoked elsewhere only has the socket as traffic; without
		// this check it would retry the handshake forever instead of returning to login.
		chatSession.setHandshakeFailedHandler(() => {
			readUsersMe()
				.then((response) => {
					if (response.response?.status === 401) appState.loadUser();
				})
				// Offline: the socket's own backoff keeps retrying.
				.catch(() => undefined);
		});
		return () => {
			chatSession.setHandshakeFailedHandler(null);
			chatSession.close();
		};
	}, []);

	// The session socket is bound to the user and organisation it was opened with.
	const sessionUserId = appState.user?.id;
	const sessionOrgId = appState.user?.active_organisation?.id;
	useEffect(() => {
		runsState.clear();
		if (sessionUserId === undefined) {
			chatSession.close();
			return;
		}
		chatSession.reconnect();
	}, [sessionUserId, sessionOrgId]);

	useEffect(() => {
		if (pathname === "/workspace/kvp" || pathname === "/workspace/kvp/") {
			navigate("/workspace/settings/kvp", { replace: true });
		}
	}, [pathname, navigate]);

	// Extract the workspace route without the /workspace prefix
	const workspaceRoute = pathname.replace(/^\/workspace\/?/, '');

	// Helper function to extract ID from route
	const extractId = (routePattern: RegExp) => {
		const match = workspaceRoute.match(routePattern);
		return match ? match[1] : null;
	};

	// More specific visibility checks
	const isDashboardVisible = workspaceRoute === '';

	const isArtifactsListVisible = workspaceRoute === 'artifacts';
	const isArtifactDetailVisible = /^artifacts\/(-1|\d+)$/.test(workspaceRoute);
	const artifactId = extractId(/^artifacts\/(-1|\d+)$/);

	// Tag ids are dash-delimited, so sibling routes must not start with `chat-`
	const isChatVisible = /^chat(-\d+)*$/.test(workspaceRoute);
	const tagIdsPart = isChatVisible ? workspaceRoute.replace('chat', '').replace(/^-/, '') : '';

	const isTagsListVisible = workspaceRoute === 'tags';
	const isTagDetailVisible = /^tags\/(-1|\d+)$/.test(workspaceRoute);
	const tagId = extractId(/^tags\/(-1|\d+)$/);

	const isEventsListVisible = workspaceRoute === 'events';
	const isEventDetailVisible = /^events\/(-1|\d+)$/.test(workspaceRoute);
	const eventId = extractId(/^events\/(-1|\d+)$/);

	const isDocumentsListVisible = workspaceRoute === 'documents';
	const isDocumentDetailVisible = /^documents\/(-1|\d+)$/.test(workspaceRoute);
	const documentId = extractId(/^documents\/(-1|\d+)$/);

	const isProfileVisible = workspaceRoute === 'profile';
	// One system page behind the gear: the tree and its inspector keep their
	// selection in the path, so every sub-route renders the same component.
	const isSettingsVisible = /^settings(\/|$)/.test(workspaceRoute);
	const isSummarisationTestsVisible = workspaceRoute === 'summarisation-tests';
	const isChatEvaluationVisible = workspaceRoute === 'chat_evaluation';
	const isChatMessageTestVisible = workspaceRoute === 'chat-test';
	const isGalleryVisible = workspaceRoute === 'gallery';
	const isBillingLabVisible = workspaceRoute === 'billing-lab';

	chatState.setMounted(isChatVisible);


	return (
		<div className="workspace-container overflow-hidden w-full h-dvh">
			{/* Dashboard - visible when at workspace root */}
			<Activity currentPath={workspaceRoute} mode={isDashboardVisible ? 'visible' : 'hidden'}>
				<WorkspaceDashboard />
			</Activity>

			{/* Artifacts routes */}
			<Activity currentPath={workspaceRoute} mode={isArtifactsListVisible ? 'visible' : 'hidden'}>
				<Artifacts />
			</Activity>

			<Activity currentPath={workspaceRoute} mode={isArtifactDetailVisible ? 'visible' : 'hidden'}>
				<ArtifactDetailWrapper id={artifactId ?? ""} />
			</Activity>

			{/* Chat routes */}
			{isChatVisible && <AdminChat tagIdsPart={tagIdsPart} />}
			{/* <Activity currentPath={workspaceRoute} mode={isChatVisible ? 'visible' : 'hidden'}>
				<AdminChat tagIdsPart={tagIdsPart} />
			</Activity> */}


			{/* Tags routes */}
			<Activity currentPath={workspaceRoute} mode={isTagsListVisible ? 'visible' : 'hidden'}>
				<Tags />
			</Activity>

			<Activity currentPath={workspaceRoute} mode={isTagDetailVisible ? 'visible' : 'hidden'}>
				<TagDetailWrapper id={tagId ?? ""} />
			</Activity>

			{/* Events routes */}
			<Activity currentPath={workspaceRoute} mode={isEventsListVisible ? 'visible' : 'hidden'}>
				<Events />
			</Activity>

			<Activity currentPath={workspaceRoute} mode={isEventDetailVisible ? 'visible' : 'hidden'}>
				<EventDetailWrapper id={eventId ?? ""} />
			</Activity>

			{/* Documents routes */}
			<Activity currentPath={workspaceRoute} mode={isDocumentsListVisible ? 'visible' : 'hidden'}>
				<Documents />
			</Activity>

			<Activity currentPath={workspaceRoute} mode={isDocumentDetailVisible ? 'visible' : 'hidden'}>
				<DocumentDetailWrapper id={documentId ?? ""} />
			</Activity>

			{/* Profile route */}
			<Activity currentPath={workspaceRoute} mode={isProfileVisible ? 'visible' : 'hidden'}>
				<ProfilePage />
			</Activity>

			{/* System settings: org tree, prompt sets, people, billing */}
			<Activity currentPath={workspaceRoute} mode={isSettingsVisible ? 'visible' : 'hidden'}>
				<SettingsPage />
			</Activity>

			<Activity currentPath={workspaceRoute} mode={isSummarisationTestsVisible ? 'visible' : 'hidden'}>
				<SummarisationTestsPage />
			</Activity>

			<Activity currentPath={workspaceRoute} mode={isChatEvaluationVisible ? 'visible' : 'hidden'}>
				<ChatEvaluationPage />
			</Activity>

			{isChatMessageTestVisible && <ChatMessageTestPage />}
			{isGalleryVisible && <GalleryPage />}
			{isBillingLabVisible && appState.user?.is_super_admin && <BillingLabPage />}
		</div>)
});


const AppRouter = observer(function AppRouter() {
	const navigate = useNavigate();

	const location = useLocation()

	useEffect(() => {
		appState.setNavigateCallback(navigate);
	}, [navigate]);


	const [pageArtifacts, setPageArtifacts] = useState<page_artifact_list_item[] | null>(null)
	const [homeArtifact, setHomeArtifact] = useState<page_artifact_list_item | null>(null)
	const [contactArtifact, setContactArtifact] = useState<page_artifact_list_item | null>(null)
	const [localModal, setLocalModal] = useState<{ title: string; body: React.ReactNode; action?: () => void } | undefined>();


	const getPageArtifacts = Request<any, any>(
		getWebPages,
		() => ({}),
		() => { },
		(data: any) => {
			const x = data.artifacts as page_artifact_list_item[]

			// Find and extract home artifact
			const homeIndex = x.findIndex(item => item.variable_name === 'home');
			let home: page_artifact_list_item | null = null;

			if (homeIndex !== -1) {
				home = x[homeIndex];
				x.splice(homeIndex, 1); // Remove home from the array
			}

			// The contact/sales page body is rendered inside /signup, not as its own CMS route.
			const contactIndex = x.findIndex(item => item.variable_name === 'contact');
			let contact: page_artifact_list_item | null = null;

			if (contactIndex !== -1) {
				contact = x[contactIndex];
				x.splice(contactIndex, 1);
			}

			// Process remaining items
			const sortedAndCleaned = x
				.map(item => {
					const match = item.variable_name.match(/^(\d+)_(.+)$/);
					return {
						...item,
						sortOrder: match ? parseInt(match[1]) : Infinity,
						variable_name: match ? match[2] : item.variable_name
					};
				})
				.sort((a, b) => a.sortOrder - b.sortOrder)
				.map(({ sortOrder, ...item }) => item);

			setHomeArtifact(home);
			setContactArtifact(contact);
			setPageArtifacts(sortedAndCleaned);
		},
		(error) => {
			setPageArtifacts([])
			setHomeArtifact(null)
			setContactArtifact(null)
		},
		() => true,
	)


	const errorHandler = (e: ErrorEvent | PromiseRejectionEvent) => {
		let message = appState.t("common.unexpectedError");
		let stack = "";
		if ('reason' in e && e.reason) {
			message = e.reason?.message || message;
			stack = e.reason?.stack || "";
		} else if ('error' in e && e.error) {
			message = e.error?.message || message;
			stack = e.error?.stack || "";
		} else if ('message' in e) {
			message = (e as ErrorEvent).message || message;
		}
		setLocalModal({
			title: message,
			body: (
				<div>
					<div className="error-title">{appState.t("common.unexpectedErrorHelp")}</div>
					<div className="error-box">{stack}</div>
					<div className="mt-2 flex flex-row justify-end">
						<ActionHandler
							title={appState.t("common.copyError")}
							funq={() => navigator.clipboard.writeText(`${message}\n${stack}`)}
						>
							{appState.t("common.copyError")}
						</ActionHandler>
					</div>
				</div>
			)
		});
		return false;
	};
	useEffect(() => {
		getPageArtifacts()

		window.addEventListener('unhandledrejection', errorHandler);
		window.addEventListener("error", errorHandler);

		return () => {
			window.removeEventListener('unhandledrejection', errorHandler);
			window.removeEventListener('error', errorHandler);
		};
	}, [])


	if (pageArtifacts != null) {

		if (appState.loadingAuth) {
			return <div className="splash hero-backdrop"><p className="l">{appState.t("marketing.bootLoading")}</p></div>;
		}


		return (
			<>
				{appState.modals.map((modal, i) => (
					<Modal key={i} get={modal} set={(val) => val === undefined ? appState.removeModal(i) : undefined} loading={appState.loadingAuth ?? false} isTop={i === appState.modals.length - 1} />
				))}
				<Modal key="local" get={localModal} set={setLocalModal} loading={appState.loadingAuth ?? false} />
				<div className="h-full w-full text-black overflow-hidden">
					<Routes>
						<Route path="/" element={
							
								<WorkspaceNavbar pages={pageArtifacts} />
								
						}>
							<Route index element={homeArtifact?.id ? <HomePage id={homeArtifact?.id} /> : (
								<PublicPageShell contentClassName="items-center justify-center gap-2 p-4">
									<p className="l">{appState.t("marketing.bootMissingHome")}</p>
									<div className="flex flex-row gap-2">
										<a className="link" href="/signin">{appState.t("marketing.navSignin")}</a>
										<a className="link" href="/signup">{appState.t("marketing.signUp")}</a>
									</div>
								</PublicPageShell>
							)}></Route>
							{pageArtifacts.map(x => {
								return <Route key={x.id} path={`${x.variable_name}`} element={<WebPage id={x.id} path={location.pathname} />}></Route>
							})}
							<Route path="standardvilkår" element={<LegalPageFallback title="Terms of Service" />} />
							<Route path="privatlivspolitik" element={<LegalPageFallback title="Privacy Policy" />} />

							<Route path="workspace/*" element={
								appState.user ?
									<Workspace />
									: <SignIn />
							} />

							<Route path="invite/:token" element={<InvitePage />} />
							<Route path="signin" element={<SignIn />} />
							<Route path="signup" element={contactArtifact?.id ? <SignUp id={contactArtifact.id} /> : (
								<PublicPageShell contentClassName="items-center justify-center gap-2 p-4">
									<p className="l">{appState.t("marketing.bootMissingContact")}</p>
									<div className="flex flex-row gap-2">
										<a className="link" href="/">{appState.t("nav.home")}</a>
										<a className="link" href="/signin">{appState.t("marketing.navSignin")}</a>
									</div>
								</PublicPageShell>
							)} />
							<Route path="priser" element={<Navigate to="/signup" replace />} />
							<Route path="pricing" element={<Navigate to="/signup" replace />} />

							<Route path="billing/return" element={<BillingReturnPage />} />
						</Route>
					</Routes>
				</div>
			</>
		);
	}
	else {

		return <div className="splash hero-backdrop"><p className="l">{appState.t("marketing.bootLoading")}</p></div>
	}

});

export default AppRouter;
